import React, { useState } from "react";
import { Form, Formik, FormikConfig } from "formik";
import { object, string, array } from "yup";
import { Entity } from "../../common/types/entity";
import { submitHelper } from "../../helpers/form";
import { Workspace } from "../../common/types/workspace";
import { UserWorkspaceRole } from "../../common/userWorkspaceRoles";
import { useInviteMemberMutation } from "../../api/workspace-member.api";
import { useToaster } from "../general/ToastMessages/useToaster";
import { StandardModalBody, StandardModalHeader } from "../general/Modal/Modal";
import { ButtonWithLoader } from "../general/ButtonWithLoader/ButtonWithLoader";
import { Button } from "../general/Button/Button";
import { AddMemberFormSchema } from "./types";
import styles from "./MemberManagement.module.scss";
import { AddTeamFields } from "./AddTeamFields";

interface Props {
    workspace: Workspace;
    entities: Entity[];
    providedEmail?: string;
    providedRole?: UserWorkspaceRole;
    closeModal: () => void;
    onInvited?: () => void;
}

export const AddMemberModal: React.FC<Props> = ({
    workspace,
    entities,
    providedEmail,
    providedRole,
    onInvited,
    closeModal,
}) => {
    const { toast } = useToaster();
    const [loading, setLoading] = useState(false);
    const inviteMemberMutation = useInviteMemberMutation(workspace);

    const form: FormikConfig<AddMemberFormSchema> = {
        initialValues: {
            emails: providedEmail ? [providedEmail] : [],
            workspaceRole: providedRole ?? UserWorkspaceRole.MEMBER,
            entityAssignments: entities.reduce(
                (acc, entity) => {
                    acc[entity.id] = true;
                    return acc;
                },
                {} as Record<number, boolean>,
            ),
            assignToNewEntities: true,
        },
        validationSchema: object().shape({
            emails: array()
                .of(string())
                .min(1, "At least one email is required")
                .required("Email is required"),
        }),
        validate({ entityAssignments, emails }) {
            if (!Object.values(entityAssignments).some((ea) => ea)) {
                return {
                    entityAssignments: "At least one entity must be selected",
                };
            }

            const emailSchema = string().email();

            if (emails.some((email) => !emailSchema.isValidSync(email))) {
                return {
                    emails: "All emails must be valid",
                };
            }
        },
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({
                emails,
                workspaceRole,
                entityAssignments,
                assignToNewEntities,
            }) => {
                await inviteMemberMutation.mutateAsync({
                    emails,
                    role: workspaceRole,
                    entityAssignments: Object.keys(entityAssignments)
                        .map(Number)
                        .filter((entityId) => entityAssignments[entityId]),
                    assignToNewEntities,
                });

                onInvited?.();
                closeModal();
                toast(`Invite sent to ${emails.join(" ,")}`);
            },
        }),
    };

    return (
        <>
            <StandardModalHeader>
                <h4 className="font-weight-bold m-0">Add team to workspace</h4>
            </StandardModalHeader>

            <StandardModalBody>
                <section className={styles.layout}>
                    <Formik {...form}>
                        {({ submitForm, isValid }) => (
                            <Form>
                                <main className={styles.teamForm}>
                                    <AddTeamFields
                                        workspace={workspace}
                                        entities={entities}
                                    />

                                    <footer className={styles.footer}>
                                        <Button
                                            variant="secondary"
                                            size="lg"
                                            onClick={closeModal}
                                        >
                                            Cancel
                                        </Button>

                                        <ButtonWithLoader
                                            loading={loading}
                                            onClick={submitForm}
                                            size="lg"
                                            disabled={!isValid}
                                        >
                                            Send invites
                                        </ButtonWithLoader>
                                    </footer>
                                </main>
                            </Form>
                        )}
                    </Formik>
                </section>
            </StandardModalBody>
        </>
    );
};
